import { useMemo, useState } from "react";
import { useGame } from "../app/GameContext";
import type { GameLogEntry } from "../shared/game-types";
import { ActionButton, ChoiceGroup, EmptyState, PageHeader, formatGameDate, formatNumber } from "../components/GameUi";

type Category = "all" | GameLogEntry["category"];

export function EventsPage() {
  const { state } = useGame();
  const [category, setCategory] = useState<Category>("all");
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => (state?.events.log ?? []).filter((entry) => (
    (category === "all" || entry.category === category)
    && (!query || `${entry.title} ${entry.detail}`.toLowerCase().includes(query.toLowerCase()))
  )), [category, query, state?.events.log]);
  if (!state) return null;
  return (
    <div className="page-stack">
      <PageHeader eyebrow="EVENT STREAM // DETERMINISTIC HAZARD" title="事件与历史" description="概率按经过时间计算，频繁刷新不会增加触发次数；重大事件会在此等待明确决策。" />
      <section aria-labelledby="pending-heading">
        <div className="section-heading branch-heading"><div><p className="eyebrow">DECISIONS REQUIRED</p><h2 id="pending-heading">待处理事件</h2></div><span className={`status-chip ${state.events.pending.length ? "warning" : ""}`}>{state.events.pending.length} 项</span></div>
        {state.events.pending.length ? <div className="event-grid">{state.events.pending.map((event) => (
          <article className="panel pending-event" key={event.id} data-event-id={event.id}>
            <header><span className="tech-id">待决事件</span><span>{formatGameDate(event.createdAtGameYear, true)}</span></header>
            <h3>{event.title}</h3><p>{event.description}</p>
            <div className="choice-list">{event.choices.map((choice) => (
              <ActionButton key={choice.id} action={{ type: "RESOLVE_EVENT", eventId: event.id, choiceId: choice.id }} className="choice-button">
                <span><b>{choice.label}</b><small>{choice.hint}</small></span><span aria-hidden="true">→</span>
              </ActionButton>
            ))}</div>
          </article>
        ))}</div> : <EmptyState>当前没有需要手动决定的重大事件。常规事件会自动结算并写入日志。</EmptyState>}
      </section>

      {state.events.reports.length > 0 && (
        <details className="panel archive-section" aria-labelledby="reports-heading">
          <summary className="archive-summary"><span><b id="reports-heading">战斗报告</b><small>展开查看每次交战的五阶段结果</small></span><span className="status-chip">{state.events.reports.length} 份</span></summary>
          <div className="report-list">{state.events.reports.map((report) => (
            <details key={report.id}>
              <summary><span><b>{report.success ? "任务成功" : "任务未达成"}</b><small>{report.summary}</small></span><span>{formatGameDate(report.createdAtGameYear, true)}</span></summary>
              <div className="combat-phases">{report.phases.map((phase) => <div key={phase.phase}><span>{phaseNames[phase.phase]}</span><b>{formatNumber(phase.playerScore)} : {formatNumber(phase.enemyScore)}</b><p>{phase.text}</p></div>)}</div>
              <p>我方损伤 {formatNumber(report.playerDamage)} · 敌方损伤 {formatNumber(report.enemyDamage)}</p>
            </details>
          ))}</div>
        </details>
      )}

      <details className="panel archive-section" aria-labelledby="history-heading">
        <summary className="archive-summary"><span><b id="history-heading">历史日志</b><small>展开筛选和检索完整文明记录</small></span><span className="status-chip">{filtered.length} / {state.events.log.length}</span></summary>
        <div className="log-controls">
          <ChoiceGroup label="筛选类别" value={category} onChange={setCategory} options={(Object.entries(categoryNames) as Array<[Category, string]>).map(([value, label]) => ({ value, label }))} />
          <label><span className="sr-only">搜索日志</span><input type="search" placeholder="搜索标题或详情" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        </div>
        {filtered.length ? <ol className="timeline">{filtered.map((entry) => (
          <li key={entry.id} className={entry.important ? "important" : ""}>
            <span className={`timeline-marker category-${entry.category}`} aria-hidden="true" />
            <div><span><b>{entry.title}</b><small>{categoryNames[entry.category]} · {formatGameDate(entry.atGameYear, true)}</small></span><p>{entry.detail}</p></div>
          </li>
        ))}</ol> : <EmptyState>没有符合当前筛选条件的日志。</EmptyState>}
      </details>
    </div>
  );
}

const categoryNames: Record<Category, string> = { all: "全部类别", economy: "经济", research: "科研", mission: "任务", diplomacy: "外交", combat: "战斗", event: "事件", system: "系统" };
const phaseNames = { detection: "探测", interception: "轨道与拦截", longRange: "远距离交战", closeRange: "近距离交战", retreat: "撤退与追击" } as const;
